import { useState, useMemo, useCallback } from 'react';
import {
	type FlagConfiguration,
	type FlagId,
	useFlagsService,
	toFlagId,
} from '@atlassian/jira-flags';
import type { CustomUIBridge } from '@atlassian/jira-forge-ui-types/src/common/types/bridge.tsx';
import type { FlagOptions } from '@atlassian/forge-ui/iframe';
import FlagMessages from './messages.tsx';

const createFlagConfiguration = (
	flagOptions: FlagOptions & {
		id: FlagId;
	},
): FlagConfiguration => {
	const { title, description, type, appearance, actions } = flagOptions;

	return {
		...flagOptions,
		messageId: `forge.forge-ui-controllers.controllers.product-flags.flag.${flagOptions.id}`,
		messageType: 'transactional',
		title: [FlagMessages.flagTitle, { title }],
		description: [FlagMessages.flagDescription, { description }],
		// fallback since both appearance and type are optional
		type: appearance ?? type ?? 'info',
		actions: actions?.map(({ text, onClick }) => ({
			onClick: () => onClick(),
			content: [FlagMessages.flagActionText, { text }],
		})),
	};
};

export const useFlagProvider = () => {
	const [flagIds, setFlagIds] = useState<Record<string, FlagId>>({});
	const { showFlag, dismissFlag } = useFlagsService();

	const createFlag = useCallback(
		(flagOptions: FlagOptions) => {
			const { id: rawId } = flagOptions;
			// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
			const id = toFlagId(rawId!);
			setFlagIds({
				...flagIds,
				// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
				[rawId!]: id,
			});
			const flagConfiguration = createFlagConfiguration({
				...flagOptions,
				id,
			});

			showFlag(flagConfiguration);
			return true;
		},
		[flagIds, showFlag],
	);

	const closeFlag = useCallback<NonNullable<CustomUIBridge['closeFlag']>>(
		({ id }) => {
			// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
			if (!flagIds[id!]) {
				return false;
			}

			// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
			dismissFlag(flagIds[id!]);
			return true;
		},
		[dismissFlag, flagIds],
	);

	return useMemo(
		() => ({
			showFlag: createFlag,
			closeFlag,
		}),
		[closeFlag, createFlag],
	);
};
